const { apiResponseToError } = require('@/lib/Api');
const { basicHydrate, createModelBasicHydrator } = require('@/lib/Hydration/basicHydrator');

/**
 * @abstract
 *
 * In the doc blocks here a "thing" means what type of thing is stored by this repository, e.g. Tickets, Products,
 * Marketing Preferences, etc.
 *
 * Your subclass needs to implement all the @abstract methods.
 *
 * If you want to use the Axios apiClient instead of old style AngularJS resources then you will also need
 * to implement loadFromApi() see GiftAidRateRepository or SessionEventRepository for examples.
 */
class AbstractRepository {
    /**
     * @param {DataStore} dataStore
     * @param {Hydrator} hydrator
     * @param {SyncStateManager} syncStateManager
     */
    constructor(
        dataStore,
        hydrator,
        syncStateManager
    ) {
        /**
         * @protected
         * @type {DataStore}
         */
        this.dataStore = dataStore;

        /**
         * @protected
         * @type {Hydrator}
         */
        this.hydrator = hydrator;

        /**
         * @protected
         * @type {SyncStateManager}
         */
        this.syncStateManager = syncStateManager;

        /**
         * @type {boolean}
         */
        this.keysAreIntegers = true;
    }

    /**
     * @abstract
     * @protected
     *
     * Return the human-readable name of the thing this repository stores.
     * e.g:
     * return "Ticket";
     * or
     * return "Kitchen Zone";
     *
     * @return {string}
     */
    getEntityName() {
        throw new Error('getEntityName has not been implemented.');
    }

    /**
     * @abstract
     * @protected
     *
     * Return the name of the Dexie store for this thing.
     * e.g:
     * return DataStore.STORE_NAMES.TICKETS;
     *
     * @return {string}
     */
    getTableName() {
        throw new Error('getTableName has not been implemented.');
    }

    /**
     * @abstract
     * @protected
     *
     * Return the class for the thing this repository stores.
     * e.g.
     * return Ticket;
     *
     * @return {function|Class}
     */
    getEntityClass() {
        throw new Error('getEntityClass has not been implemented.');
    }

    /**
     * @protected
     *
     * When you call the sync() method should the existing data in the IndexedDB table (and metadata in
     * localstorage) be cleared before inserting the freshly downloaded data?
     * Note that this does not affect what happens when the user clicks the Sync button in the menu. This is referring
     * to the sync() method in this repository which downloads data from the API and saves it, and not the "sync"
     * user-facing functionality.
     *
     * @return {boolean}
     */
    clearDataBeforeInserting() {
        return true;
    }

    /**
     * @protected
     *
     * Makes an API request to fetch all of the items that should be stored for this "thing".
     * Your method should return an array of *hydrated* models, not just the raw API data.
     *
     * @param {number|null} [branchID] - The branchID the data was requested for, where applicable.
     *
     * @return {Promise<Array>}
     */
    loadFromApi(branchID = null) {
        let resource = this.getResource();
        if (!resource || !resource.hasOwnProperty('query')) {
            throw new Error('Either getResource should return an object with a query method or you should override loadFromApi.');
        }

        return new Promise((resolve, reject) => {
            resource.query(
                {
                    branchID, // Send the requested branchID to the API so it is used instead of any default.
                },
                (response) => {
                    response = this.sanitizeDataFromApi(response);
                    resolve(this.hydrateModelsFromApi(response, branchID));
                },
                (err) => reject(apiResponseToError(err))
            );
        });
    }

    /**
     * @protected
     *
     * Receives an array of raw data from the API and returns an array of hydrated models
     * of the appropriate type for this repository.
     *
     * @param {{}[]} rawData
     * @param {number|null} [branchID] - The branchID the data was requested for, where applicable.
     *                                   Where this method is implemented in subclasses this property may be used.
     *
     * @return {Array}
     */
    // eslint-disable-next-line unused-imports/no-unused-vars
    hydrateModelsFromApi(rawData, branchID = null) {
        return this.hydrator.hydrateArray(
            rawData,
            () => new (this.getEntityClass())()
        );
    }

    /**
     * A hook for subclasses to do some modification to the response from the API before it is hydrated
     * into model.
     *
     * @param {*} rawData
     *
     * @return {*}
     */
    sanitizeDataFromApi(rawData) {
        return rawData;
    }

    /**
     * Instead of having to implement loadFromApi in every subclass you can return an Angular Resource from this method
     * in a subclass (or any object with a 'query' method') and it will be used to fetch the items from the
     * API during a sync.
     *
     * @protected
     * @return {null|{query: function(params: object, success:function, error:function)}}
     */
    getResource() {
        return null;
    }

    /**
     * Remove all rows from the table.
     *
     * @return {Promise}
     */
    clear() {
        this.syncStateManager.remove(this.getTableName());

        return this.dataStore.clear(this.getTableName());
    }

    /**
     * Get all rows from the table, ignoring their status.
     *
     * @return {Promise<[]>}
     */
    findAll() {
        return this.dataStore
            .findAll(this.getTableName())
            .then(createModelBasicHydrator(this.getEntityClass()));
    }

    /**
     * @protected
     *
     * Returns all rows from the table that have an Active status.
     *
     * This is a private method that should not be used directly - subclasses should implement a findAllActive
     * method and call this if they want to provide that method. This is because if we have a public method here
     * it can be called on repositories that don't support filtering by status (which is now all item repositories -
     * those need to be filtered by branchIDs instead.)
     *
     * @return {Promise<[]>}
     */
    _findAllActive() {
        return this.dataStore
            .findWhere(this.getTableName(), 'status', 'Active')
            .then(createModelBasicHydrator(this.getEntityClass()));
    }

    /**
     * Find the entry with the given ID in the table.
     * If found it will populate a new instance of the appropriate model and return that.
     *
     * @param {number|string} id
     *
     * @return {Promise<object>}
     */
    find(id) {
        if (this.keysAreIntegers) {
            // The IDs are stored as numbers, and Dexie does strict comparison,
            // so if we search for a string containing a number it will not be found.
            id = parseInt(id, 10);
        }

        return this.dataStore.find(this.getTableName(), id)
            .then((data) => {
                if (!data) {
                    // Throwing an error rejects the promise.
                    throw new Error(`${this.getEntityName()} ${id} was not found.`);
                }

                return basicHydrate(data, new (this.getEntityClass())());
            });
    }

    /**
     * @param {Number[]} ids
     *
     * @return {Promise<Array>}
     */
    findMany(ids) {
        if (this.keysAreIntegers) {
            // The IDs are stored as numbers, and Dexie does strict comparison,
            // so if we search for a string containing a number it will not be found.
            ids = ids.map((id) => parseInt(id));
        }

        return this.dataStore
            .getStore(this.getTableName())
            .bulkGet(ids)
            .then(createModelBasicHydrator(this.getEntityClass()));
    }

    /**
     * @param {AbstractEntity[]} models
     *
     * @return {Promise}
     */
    persistMany(models) {
        return this.dataStore.persistMany(this.getTableName(), models);
    }

    /**
     * @param {AbstractEntity} model
     *
     * @return {Promise}
     */
    persist(model) {
        return this.dataStore.persist(this.getTableName(), model);
    }

    /**
     * @param {number|null} [branchID] - The branchID the data was requested for, where applicable.
     *
     * @return {Promise}
     */
    async sync(branchID) {
        const models = await this.loadFromApi(branchID);

        if (this.clearDataBeforeInserting()) {
            await this.clear();
        }

        await this.persistMany(models);

        this.saveLastSync(branchID);
    }

    /**
     * @protected
     *
     * Saves the date and branch the last sync happened for in local storage.
     *
     * @param {number} branchID
     */
    saveLastSync(branchID) {
        this.syncStateManager.saveLastSync(this.getTableName(), branchID);
    }
}

module.exports = AbstractRepository;
